import React, { Component, type ReactNode } from 'react';

import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import noop from 'lodash/noop';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DraggableChildrenFn, DroppableStateSnapshot } from 'react-beautiful-dnd';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	layout,
	TransitionZoneIndicatorContainerProvider,
	LeftBorder,
	RightBorder,
	BoardScroll,
	type BoardScrollInfo,
	type BoardScrollInitData,
	type BoardScrollPosition,
} from '@atlassian/jira-platform-board-kit';

import { ColumnsDroppable } from './columns-droppable';

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line jira/compiled/styled-components-usages, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardContainerControl = styled.section<{
	flexibleColumns?: boolean;
	noBottomPadding?: boolean;
}>`
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: stretch;
	user-select: none;
	padding: ${(props) => {
		if (props.flexibleColumns) {
			return ff('jsw.perf.cmp-column-spacing')
				? `0 ${layout.boardBorderWidth}px ${layout.boardBorderWidth}px ${
						layout.boardBorderWidth - layout.columnMargin
					}px`
				: `0 ${layout.boardBorderWidth}px ${layout.boardBorderWidth}px ${layout.boardBorderWidth}px`;
		}
		return `0 0 ${layout.boardBorderWidth}px ${layout.boardBorderWidth - layout.cardListRadius}px`;
	}};
	${(props) => (props.noBottomPadding ? 'padding-bottom: 0' : '')};
	min-width: fit-content;
	gap: ${(props) =>
		props.flexibleColumns && !ff('jsw.perf.cmp-column-spacing')
			? `${layout.columnMargin * 2}px `
			: 0};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BoardContainerExperiment = styled2.section<{
	flexibleColumns?: boolean;
	noBottomPadding?: boolean;
}>(
	{
		display: 'flex',
		flexFlow: 'row nowrap',
		justifyContent: 'flex-start',
		alignItems: 'stretch',
		userSelect: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		padding: (props) => {
			if (props.flexibleColumns) {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				return ff('jsw.perf.cmp-column-spacing')
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						`0 ${layout.boardBorderWidth}px ${layout.boardBorderWidth}px ${
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							layout.boardBorderWidth - layout.columnMargin
						}px`
					: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						`0 ${layout.boardBorderWidth}px ${layout.boardBorderWidth}px ${layout.boardBorderWidth}px`;
			}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			return `0 0 ${layout.boardBorderWidth}px ${
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				layout.boardBorderWidth - layout.cardListRadius
			}px`;
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (props.noBottomPadding ? 'padding-bottom: 0' : ''),
	{
		minWidth: 'fit-content',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		gap: (props) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			props.flexibleColumns && !ff('jsw.perf.cmp-column-spacing')
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${layout.columnMargin * 2}px `
				: 0,
	},
);
const BoardContainer = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	BoardContainerExperiment,
	BoardContainerControl,
);

export type BoardScrollEventInfo = BoardScrollPosition & {
	reachedLeftEdge: boolean;
	reachedRightEdge: boolean;
};

type Props = {
	renderClone?: DraggableChildrenFn;
	dropZoneMode?: 'standard' | 'virtual';
	flexibleColumns?: boolean;
	children?: (
		placeholder?: ReactNode,
		snapshot?: DroppableStateSnapshot,
	) => Array<ReactNode | null> | ReactNode | null;
	offsetRightPaddingByWidth?: boolean;
	offsetLeftPaddingByWidth?: boolean;
	noBottomPadding?: boolean;
	// eslint-disable-next-line jira/react/handler-naming
	scrollRef?:
		| {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				current: null | any;
		  } // eslint-disable-next-line @typescript-eslint/no-explicit-any
		| ((arg: null | any) => unknown);
	onScroll: (scrollInfo: BoardScrollEventInfo) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onBoardView: (boardInfo: { hasHorizontalScrollbar?: boolean }) => any;
	isDropDisabled?: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class Board extends Component<Props> {
	static defaultProps = {
		offsetRightPaddingByWidth: false,
		offsetLeftPaddingByWidth: false,
		noBottomPadding: false,
		onScroll: noop,
		onBoardView: noop,
		flexibleColumns: false,
		isDropDisabled: false,
	};

	topEdge = true;

	leftEdge = true;

	rightEdge = true;

	onScrollInit = ({ hasHorizontalScrollbar }: BoardScrollInitData) => {
		this.props.onBoardView({ hasHorizontalScrollbar });
	};

	onScroll = (scrollInfo: BoardScrollInfo) => {
		const { topEdge, leftEdge, rightEdge } = this;
		if (scrollInfo.topEdge !== topEdge) {
			this.topEdge = scrollInfo.topEdge;
		}

		if (scrollInfo.leftEdge !== leftEdge) {
			this.leftEdge = scrollInfo.leftEdge;
		}

		if (scrollInfo.rightEdge !== rightEdge) {
			this.rightEdge = scrollInfo.rightEdge;
		}

		const boardScrollInfo: BoardScrollEventInfo = {
			scrollLeft: scrollInfo.scrollLeft,
			scrollTop: scrollInfo.scrollTop,
			scrollWidth: scrollInfo.scrollWidth,
			clientWidth: scrollInfo.clientWidth,
			reachedLeftEdge: !leftEdge && scrollInfo.leftEdge,
			reachedRightEdge: !rightEdge && scrollInfo.rightEdge,
		};

		this.props.onScroll(boardScrollInfo);
	};

	render() {
		const { flexibleColumns, noBottomPadding, isDropDisabled } = this.props;

		const leftBorder = !flexibleColumns && (
			<LeftBorder offsetLeftPaddingByWidth={this.props.offsetLeftPaddingByWidth} />
		);
		const rightBorder = !flexibleColumns && (
			<RightBorder offsetRightPaddingByWidth={this.props.offsetRightPaddingByWidth} />
		);

		return (
			<BoardScroll
				innerRef={this.props.scrollRef}
				onScroll={this.onScroll}
				onInit={this.onScrollInit}
				flexibleColumns={flexibleColumns}
			>
				<ColumnsDroppable
					dropZoneMode={this.props.dropZoneMode}
					renderClone={this.props.renderClone}
					isDropDisabled={isDropDisabled}
				>
					{({ placeholder, ...rest }, snapshot) => {
						const boardInnerContainer = (
							<BoardContainer
								{...rest}
								data-testid="platform-board-kit-legacy-dnd.ui.board.board-container"
								flexibleColumns={flexibleColumns}
								noBottomPadding={noBottomPadding}
							>
								{leftBorder}
								{this.props.children && this.props.children(placeholder, snapshot)}
								{rightBorder}
							</BoardContainer>
						);

						if (flexibleColumns) {
							return (
								<TransitionZoneIndicatorContainerProvider>
									{boardInnerContainer}
								</TransitionZoneIndicatorContainerProvider>
							);
						}

						return boardInnerContainer;
					}}
				</ColumnsDroppable>
			</BoardScroll>
		);
	}
}

export default Board;
